import {SupportedLocales} from '#types/locale.js';

export const marveChatLanguage = {
  [SupportedLocales.EN]: {
    accessibility: {
      chatShell: 'Marve conversation',
      messageLog: 'Conversation messages',
      scrollToLatest: 'Scroll to latest message',
      composer: 'Message Marve',
      send: 'Send message',
      connectionStatus: 'Connection status',
    },
    emptyState: 'No messages yet.',
    welcome: (name: string) =>
      name ? `Hello ${name}. How can I help?` : 'Hello. How can I help?',
    suggestions: [
      'Explain this journey to me.',
      'What are my customers doing?',
      'Help me understand AI decisioning.',
    ],
    charts: {
      unsupportedOne: 'A chart could not be displayed.',
      unsupportedMany: 'Some charts could not be displayed.',
    },
    panel: {
      title: 'AI Studio chat',
      open: 'Open AI Studio chat',
      close: 'Close AI Studio chat',
    },
    startConversation: {
      error: 'Unable to start AI Studio chat.',
    },
    resume: {
      error: 'Unable to load the existing conversation.',
      retry: 'Try again',
    },
    composer: {
      placeholder: 'Write a message',
      retry: 'Retry',
      loading: 'Preparing conversation...',
      blocked: {
        empty_message: 'Enter a message to send.',
        history_unavailable: 'Message history is unavailable.',
        context_unavailable: 'Conversation context is unavailable.',
        initial_prompt_pending: 'The initial prompt is still being sent.',
        message_already_staged: 'A message is already waiting to send.',
        ui_context_error: 'Conversation context could not be captured.',
      },
    },
    humanApproval: {
      title: 'Review requested action',
      proposedValues: 'Review proposed values',
      decisions: {
        approve: 'Approve',
        edit: 'Edit',
        reject: 'Reject',
        respond: 'Respond',
      },
      editedArguments: 'Edited tool arguments',
      response: 'Response',
      submit: 'Submit decision',
      invalidEdit: 'Enter the edited arguments as a valid JSON object.',
      responseRequired: 'Enter a response before submitting.',
      rejectionMessage:
        'The user rejected this action. Do not retry unless the user asks.',
      sendFailed: 'The decision could not be sent. Please try again.',
      invalidRequest: 'This approval request could not be displayed.',
      cancelRequest: 'Cancel request',
    },
    errors: {
      session: {
        label: 'Session error',
        message: 'The conversation session could not be loaded.',
      },
      history: {
        label: 'History error',
        message: 'Message history could not be loaded.',
      },
      contextInitialization: {
        label: 'Context initialization error',
        message: 'Conversation context could not be initialized.',
      },
      contextSubmission: {
        label: 'Context submission error',
        message: 'Conversation context could not be captured for this message.',
      },
      connection: {
        label: 'Connection error',
        message: 'The conversation connection could not be restored.',
      },
      statusWarning: {
        label: 'Status refresh warning',
        message: 'Conversation status could not be refreshed.',
      },
      protocol: {
        label: 'Message compatibility warning',
        message: 'A message from the conversation could not be displayed.',
      },
    },
    connection: {
      connecting: 'Connecting',
      connected: 'Connected',
      reconnecting: 'Reconnecting',
      exhausted: 'Connection attempts exhausted',
      disconnected: 'Disconnected',
    },
    messages: {
      user: 'You',
      agent: 'Marve',
      activity: {
        working: 'Working...',
        workedForPrefix: 'Worked for',
        usingToolPrefix: 'Using',
        ranToolPrefix: 'Ran',
        toolFailedSuffix: 'failed',
      },
      toolCalls: {
        sql: 'SQL',
        results: 'Results',
        noResults: 'No results',
      },
      toolDetails: {
        query: 'Query',
        response: 'Response',
        request: 'Request',
        noDetails: 'No details are available for this tool call.',
      },
      transaction: {
        fetchingContext: 'Fetching chat context…',
        reasoningLabel: 'Thinking',
        unableToFetchSteps:
          'Unable to fetch transaction steps. Please try your query again.',
      },
      sendFailed: 'Message failed to send.',
      insight: {
        recommendationHeading: '#### Recommendation',
      },
      audienceSuggestions: {
        created: 'An audience suggestion was created.',
        openInAiStudio: 'Review in AI Studio.',
      },
      errorMessages: {
        defaultTitle: 'We were unable to complete your request.',
        defaultMessage: 'Please try again.',
        assistantFailureTitle:
          'The assistant was unable to complete your request.',
        assistantFailureMessage:
          'Something went wrong processing your request. Please try again.',
        agentBusy: 'Wait just a moment; an agent is completing your request...',
        reload: 'Reload',
      },
    },
    runtime: {
      errors: {
        connect: 'Unable to connect to Marve',
        connectResponseInvalid: 'Marve connect response is invalid',
        connectConversationInvalid:
          'Marve connect response has an invalid conversation',
        historyUnavailable: 'Marve message history is unavailable',
        sessionStatusResponseInvalid:
          'Marve session status response is invalid',
        sessionStatusUnavailable: 'Marve session status is unavailable',
        sessionStatusInvalid: 'Marve session status is invalid',
        sessionStatusRefresh: 'Unable to refresh Marve session status',
        contextInit: 'Unable to initialize Marve context',
        contextAckTimeout: 'Marve context acknowledgement timed out',
        initialPromptContextCapture: 'Unable to capture initial prompt context',
        uiContextCapture: 'Unable to capture UI context',
        reconnect: 'Unable to reconnect to Marve',
        unsupportedActionType: (type: string) =>
          `Unsupported Marve message type: ${type}`,
      },
    },
  },
  [SupportedLocales.ES]: {
    accessibility: {
      chatShell: 'Conversación con Marve',
      messageLog: 'Mensajes de la conversación',
      scrollToLatest: 'Ir al último mensaje',
      composer: 'Mensaje para Marve',
      send: 'Enviar mensaje',
      connectionStatus: 'Estado de la conexión',
    },
    emptyState: 'Aún no hay mensajes.',
    welcome: (name: string) =>
      name
        ? `Hola ${name}. ¿Cómo puedo ayudarte?`
        : 'Hola. ¿Cómo puedo ayudarte?',
    suggestions: [
      'Explícame este journey.',
      '¿Qué están haciendo mis clientes?',
      'Ayúdame a entender la toma de decisiones con IA.',
    ],
    charts: {
      unsupportedOne: 'No se pudo mostrar un gráfico.',
      unsupportedMany: 'Algunos gráficos no se pudieron mostrar.',
    },
    panel: {
      title: 'Chat de AI Studio',
      open: 'Abrir chat de AI Studio',
      close: 'Cerrar chat de AI Studio',
    },
    startConversation: {
      error: 'No se pudo iniciar el chat de AI Studio.',
    },
    resume: {
      error: 'No se pudo cargar la conversación existente.',
      retry: 'Intentar de nuevo',
    },
    composer: {
      placeholder: 'Escribe un mensaje',
      retry: 'Reintentar',
      loading: 'Preparando la conversación...',
      blocked: {
        empty_message: 'Escribe un mensaje para enviar.',
        history_unavailable: 'El historial de mensajes no está disponible.',
        context_unavailable:
          'El contexto de la conversación no está disponible.',
        initial_prompt_pending: 'El mensaje inicial aún se está enviando.',
        message_already_staged: 'Ya hay un mensaje esperando para enviarse.',
        ui_context_error: 'No se pudo capturar el contexto de la conversación.',
      },
    },
    humanApproval: {
      title: 'Revisar la acción solicitada',
      proposedValues: 'Revisar los valores propuestos',
      decisions: {
        approve: 'Aprobar',
        edit: 'Editar',
        reject: 'Rechazar',
        respond: 'Responder',
      },
      editedArguments: 'Argumentos editados de la herramienta',
      response: 'Respuesta',
      submit: 'Enviar decisión',
      invalidEdit:
        'Ingresa los argumentos editados como un objeto JSON válido.',
      responseRequired: 'Ingresa una respuesta antes de enviar.',
      rejectionMessage:
        'El usuario rechazó esta acción. No vuelvas a intentarlo a menos que el usuario lo solicite.',
      sendFailed: 'No se pudo enviar la decisión. Inténtalo de nuevo.',
      invalidRequest: 'No se pudo mostrar esta solicitud de aprobación.',
      cancelRequest: 'Cancelar solicitud',
    },
    errors: {
      session: {
        label: 'Error de sesión',
        message: 'No se pudo cargar la sesión de conversación.',
      },
      history: {
        label: 'Error del historial',
        message: 'No se pudo cargar el historial de mensajes.',
      },
      contextInitialization: {
        label: 'Error de inicialización del contexto',
        message: 'No se pudo inicializar el contexto de la conversación.',
      },
      contextSubmission: {
        label: 'Error de envío del contexto',
        message: 'No se pudo capturar el contexto para este mensaje.',
      },
      connection: {
        label: 'Error de conexión',
        message: 'No se pudo restaurar la conexión de la conversación.',
      },
      statusWarning: {
        label: 'Advertencia de actualización del estado',
        message: 'No se pudo actualizar el estado de la conversación.',
      },
      protocol: {
        label: 'Advertencia de compatibilidad de mensajes',
        message: 'No se pudo mostrar un mensaje de la conversación.',
      },
    },
    connection: {
      connecting: 'Conectando',
      connected: 'Conectado',
      reconnecting: 'Reconectando',
      exhausted: 'Se agotaron los intentos de conexión',
      disconnected: 'Desconectado',
    },
    messages: {
      user: 'Tú',
      agent: 'Marve',
      activity: {
        working: 'Trabajando...',
        workedForPrefix: 'Trabajó durante',
        usingToolPrefix: 'Usando',
        ranToolPrefix: 'Ejecutó',
        toolFailedSuffix: 'falló',
      },
      toolCalls: {
        sql: 'SQL',
        results: 'Resultados',
        noResults: 'Sin resultados',
      },
      toolDetails: {
        query: 'Consulta',
        response: 'Respuesta',
        request: 'Solicitud',
        noDetails:
          'No hay detalles disponibles para esta llamada de herramienta.',
      },
      transaction: {
        fetchingContext: 'Obteniendo contexto del chat…',
        reasoningLabel: 'Pensando',
        unableToFetchSteps:
          'No se pudieron obtener los pasos de la transacción. Por favor intenta tu consulta nuevamente.',
      },
      sendFailed: 'No se pudo enviar el mensaje.',
      insight: {
        recommendationHeading: '#### Recomendación',
      },
      audienceSuggestions: {
        created: 'Se creó una sugerencia de audiencia.',
        openInAiStudio: 'Revisar en AI Studio.',
      },
      errorMessages: {
        defaultTitle: 'No pudimos completar tu solicitud.',
        defaultMessage: 'Por favor intenta de nuevo.',
        assistantFailureTitle: 'El asistente no pudo completar tu solicitud.',
        assistantFailureMessage:
          'Algo salió mal al procesar tu solicitud. Por favor intenta de nuevo.',
        agentBusy:
          'Espera un momento; un agente está completando tu solicitud...',
        reload: 'Recargar',
      },
    },
    runtime: {
      errors: {
        connect: 'No se pudo conectar con Marve',
        connectResponseInvalid:
          'La respuesta de conexión de Marve no es válida',
        connectConversationInvalid:
          'La respuesta de conexión de Marve tiene una conversación no válida',
        historyUnavailable:
          'El historial de mensajes de Marve no está disponible',
        sessionStatusResponseInvalid:
          'La respuesta del estado de sesión de Marve no es válida',
        sessionStatusUnavailable:
          'El estado de sesión de Marve no está disponible',
        sessionStatusInvalid: 'El estado de sesión de Marve no es válido',
        sessionStatusRefresh:
          'No se pudo actualizar el estado de sesión de Marve',
        contextInit: 'No se pudo inicializar el contexto de Marve',
        contextAckTimeout:
          'Se agotó el tiempo de espera de la confirmación del contexto de Marve',
        initialPromptContextCapture:
          'No se pudo capturar el contexto del mensaje inicial',
        uiContextCapture: 'No se pudo capturar el contexto de la UI',
        reconnect: 'No se pudo reconectar con Marve',
        unsupportedActionType: (type: string) =>
          `Tipo de mensaje de Marve no compatible: ${type}`,
      },
    },
  },
};
